import { useCallback, useEffect, useState } from "react";

import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import { useSearchParams } from "react-router-dom";

import SegmentedControl from "../../../components/SegmentedControl/SegmentedControl";
import ReceiptInbox from "../../receipts/components/ReceiptInbox/ReceiptInbox";
import ReceiptsSortDropdown from "../../receipts/components/ReceiptsSortDropdown/ReceiptsSortDropdown";
import { useCanWriteReceipts } from "../../receipts/hooks/useCanWriteReceipts";
import { useReceipt } from "../../receipts/hooks/useReceipt";
import {
  ReceiptBucket,
  ReceiptSort,
  parseReceiptSort,
} from "../../receipts/hooks/useReceiptsList";
import {
  LIVE_LEDGER_RECEIPT_BUCKET_QUERY_KEY,
  LIVE_LEDGER_RECEIPT_ID_QUERY_KEY,
  LIVE_LEDGER_RECEIPT_SORT_QUERY_KEY,
} from "../constants";

import styles from "./ReceiptInboxTab.module.scss";

const SEGMENT_OPTIONS: { value: ReceiptBucket; ftlKey: string }[] = [
  {
    value: ReceiptBucket.PENDING_REVIEW,
    ftlKey: "live-ledger-receipt-inbox-segment-pending",
  },
  {
    value: ReceiptBucket.UNMATCHED,
    ftlKey: "live-ledger-receipt-inbox-segment-unmatched",
  },
  {
    value: ReceiptBucket.MATCHED,
    ftlKey: "live-ledger-receipt-inbox-segment-matched",
  },
];

const parseBucketParam = (raw: string | null): ReceiptBucket => {
  if (raw === ReceiptBucket.UNMATCHED || raw === ReceiptBucket.MATCHED) {
    return raw;
  }
  return ReceiptBucket.PENDING_REVIEW;
};

const setBucketParam = (params: URLSearchParams, next: ReceiptBucket) => {
  if (next === ReceiptBucket.PENDING_REVIEW) {
    params.delete(LIVE_LEDGER_RECEIPT_BUCKET_QUERY_KEY);
  } else {
    params.set(LIVE_LEDGER_RECEIPT_BUCKET_QUERY_KEY, next);
  }
};

// Mirrors _BUCKET_STATUSES in public_api/views/receipts/views.py. A status
// missing from this map (or "invisible") means the receipt has no bucket a
// user can land on, which the deep-link resolver treats as not found.
const STATUS_TO_BUCKET: Record<string, ReceiptBucket | "invisible"> = {
  awaiting_review: ReceiptBucket.PENDING_REVIEW,
  confirmed: ReceiptBucket.MATCHED,
  no_match: ReceiptBucket.UNMATCHED,
  rejected: ReceiptBucket.UNMATCHED,
  orphaned: ReceiptBucket.UNMATCHED,
  extraction_failed: ReceiptBucket.UNMATCHED,
  uploaded: "invisible",
  extracting: "invisible",
  matching: "invisible",
};

const ReceiptInboxTab = ({ isActive }: { isActive: boolean }) => {
  const { l10n } = useLocalization();
  const canWrite = useCanWriteReceipts();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const bucket = parseBucketParam(
    searchParams.get(LIVE_LEDGER_RECEIPT_BUCKET_QUERY_KEY)
  );
  const sort = parseReceiptSort(
    searchParams.get(LIVE_LEDGER_RECEIPT_SORT_QUERY_KEY)
  );
  const receiptId = searchParams.get(LIVE_LEDGER_RECEIPT_ID_QUERY_KEY);

  const { data: deepLinkedReceipt, isError: isDeepLinkError } = useReceipt(
    isActive ? receiptId : null
  );

  const handleBucketChange = useCallback(
    (next: ReceiptBucket) => {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          setBucketParam(updated, next);
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSortChange = useCallback(
    (next: ReceiptSort) => {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          if (next === ReceiptSort.MOST_RECENT) {
            updated.delete(LIVE_LEDGER_RECEIPT_SORT_QUERY_KEY);
          } else {
            updated.set(LIVE_LEDGER_RECEIPT_SORT_QUERY_KEY, next);
          }
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSelectedUuidChange = useCallback(
    (uuid: string | null) => {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          if (uuid === null) {
            updated.delete(LIVE_LEDGER_RECEIPT_ID_QUERY_KEY);
          } else {
            updated.set(LIVE_LEDGER_RECEIPT_ID_QUERY_KEY, uuid);
          }
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const clearReceiptId = useCallback(
    () => handleSelectedUuidChange(null),
    [handleSelectedUuidChange]
  );

  // Resolve a deep-linked receiptId to its real bucket. A missing,
  // inaccessible, or still-processing receipt clears the param silently —
  // no toast in any fallback path.
  useEffect(() => {
    if (!isActive || receiptId === null) return;
    if (isDeepLinkError) {
      clearReceiptId();
      return;
    }
    if (!deepLinkedReceipt) return;
    const resolvedBucket = STATUS_TO_BUCKET[deepLinkedReceipt.status];
    if (resolvedBucket === undefined || resolvedBucket === "invisible") {
      clearReceiptId();
      return;
    }
    if (resolvedBucket !== bucket) {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          setBucketParam(updated, resolvedBucket);
          return updated;
        },
        { replace: true }
      );
    }
  }, [
    isActive,
    receiptId,
    isDeepLinkError,
    deepLinkedReceipt,
    bucket,
    clearReceiptId,
    setSearchParams,
  ]);

  // Only hand the uuid down once its resolved bucket matches the segment
  // actually on screen — otherwise it would flash open under the wrong
  // segment while the bucket-sync effect above is still catching up.
  const deepLinkedUuid =
    isActive &&
    receiptId !== null &&
    deepLinkedReceipt &&
    !isDeepLinkError &&
    STATUS_TO_BUCKET[deepLinkedReceipt.status] === bucket
      ? receiptId
      : null;

  const segmentOptions = SEGMENT_OPTIONS.map((option) => ({
    value: option.value,
    label: l10n.getString(option.ftlKey),
  }));

  return (
    <div data-testid="live-ledger-receipt-inbox-tab">
      <div className={styles["control-row"]}>
        <SegmentedControl
          options={segmentOptions}
          value={bucket}
          onChange={handleBucketChange}
          testId="live-ledger-receipt-inbox-segments"
          ariaLabel={l10n.getString("live-ledger-receipt-inbox-segments-label")}
        />
        <div className={styles["control-row-actions"]}>
          <ReceiptsSortDropdown value={sort} onChange={handleSortChange} />
          {canWrite && (
            <Button
              kind="primary"
              startIcon="plus"
              label={l10n.getString("receipts-upload-button")}
              onClick={() => setIsUploadDialogOpen(true)}
              testId="live-ledger-receipt-inbox-upload-button"
            />
          )}
        </div>
      </div>
      {isActive && (
        <ReceiptInbox
          bucket={bucket}
          isUploadDialogOpen={isUploadDialogOpen}
          onUploadDialogOpenChange={setIsUploadDialogOpen}
          hideSortRow
          deepLinkedUuid={deepLinkedUuid}
          onSelectedUuidChange={handleSelectedUuidChange}
        />
      )}
    </div>
  );
};

export default ReceiptInboxTab;
